import { AlertTriangle, CheckCircle2, Info, OctagonAlert } from "lucide-react";
import { useState, useSyncExternalStore, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { useUiText } from "../lib/locale";
import { Button } from "./button";
import { Field } from "./field";
import { Input } from "./input";
import { Modal } from "./modal";

export type Tone = "info" | "success" | "warning" | "danger";

const toneIcon: Record<Tone, ReactNode> = {
  info: <Info />,
  success: <CheckCircle2 />,
  warning: <AlertTriangle />,
  danger: <OctagonAlert />,
};
const toneClass: Record<Tone, string> = {
  info: "bg-info-soft text-info",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  danger: "bg-danger-soft text-danger",
};

/** Figma «Popup / confirmation»: заголовок 20/semibold и описание 14/muted по центру; иконка — только для warning/danger/success. */
function ToneHeader({ tone, title, description }: { tone: Tone; title: ReactNode; description?: ReactNode }) {
  const showIcon = tone !== "info";
  return (
    <div className="flex flex-col items-center gap-3 pt-2 text-center">
      {showIcon && <span className={cn("grid size-14 shrink-0 place-items-center rounded-full [&_svg]:size-7", toneClass[tone])}>{toneIcon[tone]}</span>}
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="text-xl leading-normal font-semibold">{title}</h2>
        {description && <div className="text-sm leading-[1.4] whitespace-pre-line text-muted">{description}</div>}
      </div>
    </div>
  );
}

export type AlertModalProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  tone?: Tone;
  okText?: string;
  children?: ReactNode;
};

/** Информационное сообщение с одной кнопкой. */
export function AlertModal({ open, onClose, title, description, tone = "info", okText, children }: AlertModalProps) {
  const text = useUiText();
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} size="sm" hideClose ariaTitle={typeof title === "string" ? title : undefined} footer={<Button autoFocus onClick={onClose}>{okText ?? text.ok}</Button>}>
      <ToneHeader tone={tone} title={title} description={description} />
      {children && <div className="mt-4">{children}</div>}
    </Modal>
  );
}

export type ConfirmModalProps = {
  open: boolean;
  onCancel: () => void;
  /** Может быть async: кнопка показывает загрузку, модалка закрывается после успеха; ошибка показывается внутри. */
  onConfirm: () => void | Promise<void>;
  title: ReactNode;
  description?: ReactNode;
  tone?: Tone;
  confirmText?: string;
  cancelText?: string;
  /** Для необратимых действий: пользователь должен ввести этот текст (например, название филиала). */
  requireText?: string;
  children?: ReactNode;
};

export function ConfirmModal({
  open, onCancel, onConfirm, title, description, tone = "warning", confirmText, cancelText, requireText, children,
}: ConfirmModalProps) {
  const text = useUiText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const blocked = !!requireText && typed.trim() !== requireText;

  const reset = () => { setBusy(false); setError(null); setTyped(""); };
  const cancel = () => { if (!busy) { reset(); onCancel(); } };
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      reset();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : text.actionFailed);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && cancel()}
      size="sm"
      hideClose
      ariaTitle={typeof title === "string" ? title : undefined}
      dismissible={!busy}
      footer={
        <>
          <Button variant={tone === "danger" ? "danger" : "primary"} loading={busy} disabled={blocked} onClick={confirm} autoFocus={!requireText && tone !== "danger"}>
            {confirmText ?? (tone === "danger" ? text.delete : text.confirm)}
          </Button>
          <Button variant="outline" disabled={busy} onClick={cancel} autoFocus={!requireText && tone === "danger"}>
            {cancelText ?? text.cancel}
          </Button>
        </>
      }
    >
      <ToneHeader tone={tone} title={title} description={description} />
      {children && <div className="mt-4">{children}</div>}
      {requireText && (
        <Field className="mt-4" label={<>{text.typeToConfirmBefore} <b className="select-all">{requireText}</b> {text.typeToConfirmAfter}</>}>
          <Input autoFocus autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !blocked && confirm()} />
        </Field>
      )}
      {error && <p role="alert" className="mt-3 rounded-tile bg-danger-soft px-3 py-2 text-sm text-danger">{error}</p>}
    </Modal>
  );
}

/* ---------- Императивный API: await dialogs.confirm({...}) из любого места (нужен <DialogHost/> в корне) ---------- */

type AlertOpts = Omit<AlertModalProps, "open" | "onClose">;
type ConfirmOpts = Omit<ConfirmModalProps, "open" | "onCancel" | "onConfirm"> & { onConfirm?: () => void | Promise<void> };
type Entry =
  | { id: number; kind: "alert"; opts: AlertOpts; resolve: (v: void) => void }
  | { id: number; kind: "confirm"; opts: ConfirmOpts; resolve: (v: boolean) => void };

let queue: Entry[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const push = (e: Entry) => { queue = [...queue, e]; emit(); };
const pop = (id: number) => { queue = queue.filter((e) => e.id !== id); emit(); };

export const dialogs = {
  alert: (opts: AlertOpts) => new Promise<void>((resolve) => push({ id: ++seq, kind: "alert", opts, resolve })),
  /** true — подтверждено (и onConfirm успешно выполнен), false — отмена. */
  confirm: (opts: ConfirmOpts) => new Promise<boolean>((resolve) => push({ id: ++seq, kind: "confirm", opts, resolve })),
  /** Шорткат для удаления/аннулирования. */
  confirmDanger: (opts: ConfirmOpts) => dialogs.confirm({ tone: "danger", ...opts }),
};

export function DialogHost() {
  const items = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => queue,
    () => queue,
  );
  const top = items[0];
  if (!top) return null;
  if (top.kind === "alert")
    return <AlertModal key={top.id} open {...top.opts} onClose={() => { pop(top.id); top.resolve(); }} />;
  return (
    <ConfirmModal
      key={top.id}
      open
      {...top.opts}
      onCancel={() => { pop(top.id); top.resolve(false); }}
      onConfirm={async () => { await top.opts.onConfirm?.(); pop(top.id); top.resolve(true); }}
    />
  );
}
